import React, { useState, useEffect, useRef } from 'react';
import '../CSS/JobUpload.css';
import '../CSS/DirectorySearch.css';

interface Subscriber {
  email: string;
  firstName: string;
  lastName: string;
  createdDate?: string;
  lastUpdated?: string;
}

type SortField = 'email' | 'firstName' | 'lastName' | 'createdDate' | 'lastUpdated';
type SortDirection = 'asc' | 'desc';

const SubscriberDirectory: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [filteredSubscribers, setFilteredSubscribers] = useState<Subscriber[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>('createdDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedSubscribers, setSelectedSubscribers] = useState<Set<string>>(new Set());
  const [showFilterPopup, setShowFilterPopup] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const filterPopupRef = useRef<HTMLDivElement>(null);

  // Fetch subscribers from API
  useEffect(() => {
    const fetchSubscribers = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
        // Production API endpoint
        // const response = await fetch('/api/admin/subscriber-information', {
        const response = await fetch('/api/admin/subscriber-information', {
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {}
        });
        const data = await response.json();

        if (data.success && data.data) {
          const subscribersData: Subscriber[] = data.data.map((subscriber: any) => ({
            email: subscriber.email || '',
            firstName: subscriber.firstName || '',
            lastName: subscriber.lastName || '',
            createdDate: subscriber.createdDate || '',
            lastUpdated: subscriber.lastUpdated || ''
          }));
          setSubscribers(subscribersData);
          setFilteredSubscribers(subscribersData);
        } else {
          setSubscribers([]);
          setFilteredSubscribers([]);
        }
      } catch (error) {
        console.error('Error fetching subscribers:', error);
        setSubscribers([]);
        setFilteredSubscribers([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscribers();
  }, []);

  // Filter subscribers based on search query
  useEffect(() => {
    let filtered = [...subscribers];

    // Apply search filter
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(subscriber =>
        subscriber.email.toLowerCase().includes(query) ||
        subscriber.firstName.toLowerCase().includes(query) ||
        subscriber.lastName.toLowerCase().includes(query)
      );
    }

    // Apply date filter
    if (startDate || endDate) {
      filtered = filtered.filter(subscriber => {
        if (!subscriber.createdDate) return false;
        
        const createdDate = new Date(subscriber.createdDate);
        const start = startDate ? new Date(startDate) : null;
        const end = endDate ? new Date(endDate) : new Date(); // If no end date, use current date
        
        if (start && end) {
          // Both dates provided: filter between dates
          return createdDate >= start && createdDate <= end;
        } else if (start) {
          // Only start date: filter from start to now
          return createdDate >= start;
        } else if (end) {
          // Only end date: filter all records older than end date
          return createdDate <= end;
        }
        return true;
      });
    }

    setFilteredSubscribers(filtered);
    setCurrentPage(1);
  }, [searchQuery, subscribers, startDate, endDate]);

  // Sort subscribers
  const sortedSubscribers = [...filteredSubscribers].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    if (sortField === 'createdDate' || sortField === 'lastUpdated') {
      aValue = new Date(aValue || 0).getTime();
      bValue = new Date(bValue || 0).getTime();
    } else {
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedSubscribers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedSubscribers = sortedSubscribers.slice(startIndex, startIndex + itemsPerPage);

  // Handle sort
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle checkbox selection
  const handleSelectSubscriber = (email: string) => {
    const newSelected = new Set(selectedSubscribers);
    if (newSelected.has(email)) {
      newSelected.delete(email);
    } else {
      newSelected.add(email);
    }
    setSelectedSubscribers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedSubscribers.size === paginatedSubscribers.length) {
      setSelectedSubscribers(new Set());
    } else {
      setSelectedSubscribers(new Set(paginatedSubscribers.map(subscriber => subscriber.email)));
    }
  };

  // Close filter popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterPopupRef.current && !filterPopupRef.current.contains(event.target as Node)) {
        setShowFilterPopup(false);
      }
    };

    if (showFilterPopup) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterPopup]);

  // Handle filter apply
  const handleApplyFilter = () => {
    setShowFilterPopup(false);
    // Filtering is handled by useEffect
  };

  // Handle clear filter
  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setShowFilterPopup(false);
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (sortedSubscribers.length === 0) {
      alert('No subscribers to export');
      return;
    }

    const headers = ['Email', 'First Name', 'Last Name', 'Created Date', 'Last Updated'];
    const csvRows = [
      headers.join(','),
      ...sortedSubscribers.map(subscriber => {
        const createdDate = subscriber.createdDate 
          ? new Date(subscriber.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : '';
        const createdTime = subscriber.createdDate
          ? new Date(subscriber.createdDate).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
          : '';
        const createdOn = createdDate && createdTime ? `${createdDate} at ${createdTime}` : createdDate || '';
        
        const updatedDate = subscriber.lastUpdated 
          ? new Date(subscriber.lastUpdated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          : '';
        const updatedTime = subscriber.lastUpdated
          ? new Date(subscriber.lastUpdated).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
          : '';
        const updatedOn = updatedDate && updatedTime ? `${updatedDate} at ${updatedTime}` : updatedDate || '';
        
        return [
          `"${subscriber.email.replace(/"/g, '""')}"`,
          `"${subscriber.firstName.replace(/"/g, '""')}"`,
          `"${subscriber.lastName.replace(/"/g, '""')}"`,
          `"${createdOn}"`,
          `"${updatedOn}"`
        ].join(',');
      })
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `subscribers-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString: string): string => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  return (
    <div className="job-upload-page">
      <div className="job-upload-container" style={{ width: '90vw', maxWidth: 'none' }}>
        {/* Header with Search, Filter, and Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', gap: '1rem', flexWrap: 'wrap' }}>
          {/* Search Bar */}
          <div style={{ flex: '1', minWidth: '300px', position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by Email, First Name or Last Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 2.5rem',
                border: '1px solid #d0d0d0',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif'
              }}
            />
            <svg
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                pointerEvents: 'none'
              }}
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M19 19L13 13M15 8C15 11.866 11.866 15 8 15C4.13401 15 1 11.866 1 8C1 4.13401 4.13401 1 8 1C11.866 1 15 4.13401 15 8Z"
                stroke="#666666"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          {/* Filter Button */}
          <div style={{ position: 'relative' }} ref={filterPopupRef}>
            <button
              type="button"
              onClick={() => setShowFilterPopup(!showFilterPopup)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#ffffff',
                color: '#000000',
                border: '1px solid #d0d0d0',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 4H14M4 8H12M6 12H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Filter
            </button>
            {showFilterPopup && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  marginTop: '0.5rem',
                  backgroundColor: '#ffffff',
                  border: '1px solid #d0d0d0',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  zIndex: 9999,
                  minWidth: '300px',
                  padding: '1.5rem'
                }}
              >
                <h3 style={{ margin: '0 0 1rem 0', fontFamily: 'Poppins, sans-serif', fontSize: '1rem', fontWeight: '600', letterSpacing: '0.05em' }}>
                  Filter by Created Date
                </h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', fontWeight: '500' }}>
                      Start Date
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontFamily: 'Lato, sans-serif', fontSize: '0.9rem', fontWeight: '500' }}>
                      End Date
                    </label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontFamily: 'Lato, sans-serif',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={handleApplyFilter}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: '#0E4877',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        fontFamily: 'Lato, sans-serif',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Apply Filter
                    </button>
                    <button
                      type="button"
                      onClick={handleClearFilter}
                      style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: '#ffffff',
                        color: '#000000',
                        border: '1px solid #d0d0d0',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        fontFamily: 'Lato, sans-serif',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleExportCSV}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#0E4877',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontFamily: 'Lato, sans-serif',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2V11M8 11L5 8M8 11L11 8M2 13H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Export as .CSV
            </button>
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div style={{ padding: '2rem', textAlign: 'left' }}>Loading subscribers...</div>
        ) : (
          <>
            <div style={{ border: '1px solid #d0d0d0', borderRadius: '8px', overflow: 'visible', backgroundColor: '#ffffff' }}>
              {/* Table Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '40px 2fr 1.5fr 1.5fr 200px 200px',
                backgroundColor: '#16416F',
                color: '#ffffff',
                fontFamily: 'Poppins, sans-serif',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selectedSubscribers.size === paginatedSubscribers.length && paginatedSubscribers.length > 0}
                    onChange={handleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('email')}
                >
                  Email
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'email' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'email' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('firstName')}
                >
                  First Name
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'firstName' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'firstName' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('lastName')}
                >
                  Last Name
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'lastName' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'lastName' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('createdDate')}
                >
                  Created Date
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'createdDate' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'createdDate' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
                <div
                  style={{ padding: '1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
                  onClick={() => handleSort('lastUpdated')}
                >
                  Last Updated
                  <span style={{ display: 'flex', flexDirection: 'column', fontSize: '0.7rem' }}>
                    <span style={{ opacity: sortField === 'lastUpdated' && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                    <span style={{ opacity: sortField === 'lastUpdated' && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                  </span>
                </div>
              </div>

              {/* Table Rows */}
              {paginatedSubscribers.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'left', color: '#666' }}>No subscribers found</div>
              ) : (
                paginatedSubscribers.map((subscriber) => (
                  <div
                    key={subscriber.email}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px 2fr 1.5fr 1.5fr 200px 200px',
                      borderTop: '1px solid #e0e0e0',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#ffffff';
                    }}
                  >
                    <div style={{ padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedSubscribers.has(subscriber.email)}
                        onChange={() => handleSelectSubscriber(subscriber.email)}
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {subscriber.email}
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {subscriber.firstName}
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {subscriber.lastName}
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {subscriber.createdDate ? `${formatDate(subscriber.createdDate)} at ${formatTime(subscriber.createdDate)}` : ''}
                    </div>
                    <div style={{ padding: '1rem', fontFamily: 'Lato, sans-serif', fontSize: '1rem', textAlign: 'left' }}>
                      {subscriber.lastUpdated ? `${formatDate(subscriber.lastUpdated)} at ${formatTime(subscriber.lastUpdated)}` : ''}
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Pagination */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '1.5rem',
              padding: '1rem 0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem' }}>Items per page:</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '0.5rem',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    fontFamily: 'Lato, sans-serif',
                    fontSize: '0.9rem',
                    cursor: 'pointer'
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div style={{ fontFamily: 'Lato, sans-serif', fontSize: '0.9rem' }}>
                Page {currentPage} of {totalPages || 1}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.5rem',
                    background: 'none',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  style={{
                    padding: '0.5rem',
                    background: 'none',
                    border: '1px solid #d0d0d0',
                    borderRadius: '4px',
                    cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                    opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriberDirectory;

